import api, { API_BASE } from './api';
import {
    Branch,
    BranchPayload,
    Customer,
    CustomerPayload,
    ListTag,
    Project,
    ProjectPayload,
    Vendor,
    VendorPayload,
} from '../types/lists.types';

/**
 * Lists module API — Customers, Vendors, Branches.
 *
 * Records are never deleted; the only lifecycle change is the Active/Inactive
 * toggle, so there are no delete helpers here by design.
 */

const query = (params: Record<string, string | undefined>) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v) q.append(k, v);
    });
    const s = q.toString();
    return s ? `?${s}` : '';
};

/** Streams an export straight to the browser as a file download. */
const downloadExport = async (path: string, format: 'csv' | 'excel', filename: string) => {
    const token = localStorage.getItem('token');
    const base = API_BASE.replace(/\/$/, '');

    const response = await fetch(`${base}${path}${query({ format })}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Failed to export data');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

/* -------------------------------------------------------------- customers */

export const customersApi = {
    list: (params: { q?: string; status?: string } = {}) =>
        api.get<Customer[]>(`/api/lists/customers${query(params)}`),

    get: (id: number) => api.get<Customer>(`/api/lists/customers/${id}`),

    create: (body: CustomerPayload) => api.post<Customer>('/api/lists/customers', body),

    update: (id: number, body: Partial<CustomerPayload>) =>
        api.patch<Customer>(`/api/lists/customers/${id}`, body),

    setStatus: (id: number, isActive: boolean) =>
        api.patch<Customer>(`/api/lists/customers/${id}/status`, { isActive }),

    categories: () => api.get<ListTag[]>('/api/lists/customers/categories'),

    export: (format: 'csv' | 'excel') =>
        downloadExport('/api/lists/customers/export', format, 'customers'),
};

/* ---------------------------------------------------------------- vendors */

export const vendorsApi = {
    list: (params: { q?: string; status?: string; module?: string } = {}) =>
        api.get<Vendor[]>(`/api/lists/vendors${query(params)}`),

    get: (id: number) => api.get<Vendor>(`/api/lists/vendors/${id}`),

    create: (body: VendorPayload) => api.post<Vendor>('/api/lists/vendors', body),

    update: (id: number, body: Partial<VendorPayload>) =>
        api.patch<Vendor>(`/api/lists/vendors/${id}`, body),

    setStatus: (id: number, isActive: boolean) =>
        api.patch<Vendor>(`/api/lists/vendors/${id}/status`, { isActive }),

    tags: () => api.get<ListTag[]>('/api/lists/vendors/tags'),

    export: (format: 'csv' | 'excel') =>
        downloadExport('/api/lists/vendors/export', format, 'vendors'),
};

/* --------------------------------------------------------------- branches */

export const branchesApi = {
    list: (params: { q?: string; status?: string } = {}) =>
        api.get<Branch[]>(`/api/lists/branches${query(params)}`),

    get: (id: number) => api.get<Branch>(`/api/lists/branches/${id}`),

    create: (body: BranchPayload) => api.post<Branch>('/api/lists/branches', body),

    update: (id: number, body: Partial<BranchPayload>) =>
        api.patch<Branch>(`/api/lists/branches/${id}`, body),

    setStatus: (id: number, isActive: boolean) =>
        api.patch<Branch>(`/api/lists/branches/${id}/status`, { isActive }),
};

/* --------------------------------------------------------------- projects */

export const projectsApi = {
    list: (params: { q?: string; status?: string; branchId?: string; customerId?: string } = {}) =>
        api.get<Project[]>(`/api/lists/projects${query(params)}`),

    get: (id: number) => api.get<Project>(`/api/lists/projects/${id}`),

    create: (body: ProjectPayload) => api.post<Project>('/api/lists/projects', body),

    update: (id: number, body: Partial<ProjectPayload>) =>
        api.patch<Project>(`/api/lists/projects/${id}`, body),

    setStatus: (id: number, isActive: boolean) =>
        api.patch<Project>(`/api/lists/projects/${id}/status`, { isActive }),

    export: (format: 'csv' | 'excel') =>
        downloadExport('/api/lists/projects/export', format, 'projects'),
};
